import { useState, useEffect } from 'react'
import { Users, Plus, Search, Download, Trash2, Edit2, Eye, UserX, Calendar, Mail, Hash } from 'lucide-react'
import { endUsersApi, EndUser, EndUserStats } from '../lib/api'
import { useAppLanguage } from '../i18n'

export default function EndUsersPage() {
  const { t, language } = useAppLanguage()
  const [endUsers, setEndUsers] = useState<EndUser[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState<EndUser | null>(null)
  const [userStats, setUserStats] = useState<EndUserStats | null>(null)
  const [showStatsModal, setShowStatsModal] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)

  useEffect(() => {
    loadEndUsers()
  }, [])

  const loadEndUsers = async () => {
    setLoading(true)
    try {
      const res = await endUsersApi.list({ limit: 100 })
      setEndUsers(res.items)
    } catch (error) {
      console.error('Failed to load end-users:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadEndUsers()
      return
    }
    
    setLoading(true)
    try {
      const res = await endUsersApi.list({ search: searchQuery, limit: 100 })
      setEndUsers(res.items)
    } catch (error) {
      console.error('Failed to search end-users:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleViewStats = async (user: EndUser) => {
    try {
      const stats = await endUsersApi.getStats(user.id)
      setUserStats(stats)
      setSelectedUser(user)
      setShowStatsModal(true)
    } catch (error) {
      console.error('Failed to load stats:', error)
      alert(t.endUsers.errors.loadStats)
    }
  }

  const handleExport = async (user: EndUser) => {
    try {
      const data = await endUsersApi.exportData(user.id)
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `end-user-${user.id}-export.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to export data:', error)
      alert(t.endUsers.errors.export)
    }
  }

  const handleDelete = async (user: EndUser) => {
    const name = user.display_name || user.external_email || `User #${user.id}`
    if (!confirm(`${t.endUsers.confirm.delete.replace('{name}', name)}\n\n${t.endUsers.confirm.deleteWarning}`)) {
      return
    }
    
    try {
      await endUsersApi.delete(user.id)
      await loadEndUsers()
    } catch (error) {
      console.error('Failed to delete end-user:', error)
      alert(t.endUsers.errors.delete)
    }
  }

  const handleAnonymize = async (user: EndUser) => {
    const name = user.display_name || user.external_email || `User #${user.id}`
    if (!confirm(`${t.endUsers.confirm.anonymize.replace('{name}', name)}\n\n${t.endUsers.confirm.anonymizeWarning}`)) {
      return
    }
    
    try {
      await endUsersApi.anonymize(user.id)
      await loadEndUsers()
    } catch (error) {
      console.error('Failed to anonymize end-user:', error)
      alert(t.endUsers.errors.anonymize)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return t.endUsers.labels.never
    const date = new Date(dateStr)
    const locale = language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : 'en-US'
    return date.toLocaleDateString(locale, { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const filteredUsers = endUsers.filter(user => {
    if (!searchQuery) return true
    const search = searchQuery.toLowerCase()
    return (
      user.display_name?.toLowerCase().includes(search) ||
      user.external_email?.toLowerCase().includes(search) ||
      user.external_id?.toLowerCase().includes(search) ||
      user.external_username?.toLowerCase().includes(search)
    )
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen">
      {/* Background */}
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />

      <div className="relative mx-auto max-w-[1600px] py-3 sm:py-6">

        {/* Search & Actions */}
        <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-3xl border border-purple-200/50 dark:border-purple-800/30 p-4 mb-4">
          {/* Ligne 1 : champ + bouton Nouveau */}
          <div className="flex items-center gap-2">
            <div className="flex-1 relative min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder={t.endUsers.searchPlaceholder}
                className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleSearch}
              className="shrink-0 px-3 py-2.5 rounded-xl bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-sm font-medium hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
            >
              {t.endUsers.search}
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-full bg-gradient-to-r from-purple-500 to-purple-600 text-white text-sm font-medium shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 transition-all"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">{t.endUsers.newClient}</span>
              <span className="sm:hidden">{t.endUsers.newClient}</span>
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-purple-200/50 dark:border-purple-800/30 p-3 sm:p-5">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-purple-600 dark:text-purple-400 shrink-0" />
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 truncate">{t.endUsers.stats.totalClients}</span>
            </div>
            <div className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{endUsers.length}</div>
          </div>
          <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-purple-200/50 dark:border-purple-800/30 p-3 sm:p-5">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 truncate">{t.endUsers.stats.active7d}</span>
            </div>
            <div className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
              {endUsers.filter(u => {
                if (!u.last_seen_at) return false
                const lastSeen = new Date(u.last_seen_at)
                const weekAgo = new Date()
                weekAgo.setDate(weekAgo.getDate() - 7)
                return lastSeen > weekAgo
              }).length}
            </div>
          </div>
          <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-purple-200/50 dark:border-purple-800/30 p-3 sm:p-5">
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 truncate">{t.endUsers.stats.withEmail}</span>
            </div>
            <div className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
              {endUsers.filter(u => u.external_email).length}
            </div>
          </div>
        </div>

        {/* End-Users List */}
        <div className="grid grid-cols-1 gap-4">
          {filteredUsers.length === 0 ? (
            <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-3xl border border-purple-200/50 dark:border-purple-800/30 p-10 text-center">
              <Users className="w-12 h-12 mx-auto mb-3 text-zinc-400" />
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                {searchQuery ? t.endUsers.empty.noResults : t.endUsers.empty.noClients}
              </p>
            </div>
          ) : (
            filteredUsers.map(user => (
              <div
                key={user.id}
                className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-purple-200/50 dark:border-purple-800/30 p-4 hover:shadow-lg transition-all"
              >
                {/* Ligne 1 : avatar + nom + actions */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 shrink-0 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">
                    {user.display_name?.[0]?.toUpperCase() || user.external_email?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                      {user.display_name || user.external_email || user.external_username || `User #${user.id}`}
                    </h3>
                    {user.external_email && user.display_name !== user.external_email && (
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{user.external_email}</p>
                    )}
                  </div>
                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => handleViewStats(user)}
                      className="p-1.5 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                      title={t.endUsers.tooltips.stats}>
                      <Eye className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    </button>
                    <button onClick={() => handleExport(user)}
                      className="p-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      title={t.endUsers.tooltips.export}>
                      <Download className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </button>
                    <button onClick={() => handleAnonymize(user)}
                      className="p-1.5 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                      title={t.endUsers.tooltips.anonymize}>
                      <UserX className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    </button>
                    <button onClick={() => handleDelete(user)}
                      className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      title={t.endUsers.tooltips.delete}>
                      <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </button>
                  </div>
                </div>

                {/* Ligne 2 : métadonnées */}
                <div className="mt-2 pl-12 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500 dark:text-zinc-400">
                  {user.external_id && (
                    <span className="flex items-center gap-1">
                      <Hash className="w-3 h-3" />{user.external_id}
                    </span>
                  )}
                  {user.external_username && (
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />@{user.external_username}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />{formatDate(user.created_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3" />{formatDate(user.last_seen_at)}
                  </span>
                </div>

                {user.metadata && Object.keys(user.metadata).length > 0 && (
                  <div className="mt-2 pl-12 flex flex-wrap gap-1.5">
                    {Object.entries(user.metadata).slice(0, 3).map(([key, value]) => (
                      <span key={key} className="px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-xs text-zinc-600 dark:text-zinc-400">
                        {key}: {String(value)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Stats Modal */}
      {showStatsModal && userStats && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl max-w-2xl w-full">
            <div className="border-b border-zinc-200 dark:border-zinc-800 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                {t.endUsers.modal.statsTitle} - {userStats.display_name}
              </h2>
              <button
                onClick={() => setShowStatsModal(false)}
                className="p-2 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
              >
                <Eye className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-purple-50 dark:bg-purple-900/20">
                  <div className="text-sm text-purple-600 dark:text-purple-400 mb-1">{t.endUsers.modal.memoryScopes}</div>
                  <div className="text-3xl font-bold text-purple-700 dark:text-purple-300">
                    {userStats.memory_scopes_count}
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20">
                  <div className="text-sm text-emerald-600 dark:text-emerald-400 mb-1">{t.endUsers.modal.memoryFacts}</div>
                  <div className="text-3xl font-bold text-emerald-700 dark:text-emerald-300">
                    {userStats.memory_facts_count}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-zinc-200 dark:border-zinc-800">
                  <span className="text-zinc-600 dark:text-zinc-400">{t.endUsers.modal.externalId}</span>
                  <span className="font-medium text-zinc-900 dark:text-zinc-100">{userStats.external_id || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-200 dark:border-zinc-800">
                  <span className="text-zinc-600 dark:text-zinc-400">{t.endUsers.modal.email}</span>
                  <span className="font-medium text-zinc-900 dark:text-zinc-100">{userStats.external_email || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-200 dark:border-zinc-800">
                  <span className="text-zinc-600 dark:text-zinc-400">{t.endUsers.modal.createdAt}</span>
                  <span className="font-medium text-zinc-900 dark:text-zinc-100">{formatDate(userStats.created_at)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-zinc-600 dark:text-zinc-400">{t.endUsers.modal.lastActivity}</span>
                  <span className="font-medium text-zinc-900 dark:text-zinc-100">{formatDate(userStats.last_seen_at)}</span>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowStatsModal(false)
                    handleExport(selectedUser)
                  }}
                  className="flex-1 px-6 py-3 rounded-full border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  {t.endUsers.tooltips.export}
                </button>
                <button
                  onClick={() => setShowStatsModal(false)}
                  className="flex-1 px-6 py-3 rounded-full bg-gradient-to-r from-purple-500 to-purple-600 text-white font-medium shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 transition-all"
                >
                  {t.endUsers.modal.close}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal - Placeholder */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-zinc-900 rounded-3xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">{t.endUsers.modal.newClientTitle}</h2>
            <p className="text-zinc-600 dark:text-zinc-400 mb-4">
              {t.endUsers.modal.newClientInfo}
              <br /><br />
              {t.endUsers.modal.newClientApi} <code className="px-2 py-1 bg-zinc-100 dark:bg-zinc-800 rounded">/api/end-users</code>
            </p>
            <button
              onClick={() => setShowCreateModal(false)}
              className="w-full px-4 py-2 rounded-full bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 transition-colors"
            >
              {t.endUsers.modal.close}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
